import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { CreateProjectHeader } from '@/features/projects/create-project/components/layouts/create-project-header';
import {
  CreateProjectStoreSetData,
  useCreateProjectStore,
} from '@/features/projects/create-project/store/use-create-project-store';
import PlusWhiteIcon from '@/shared/assets/plus-white.svg?react';
import { BorderGradient } from '@/shared/components/border-gradient';
import {
  FlowBackButton,
  FlowSkipStepButton,
} from '@/shared/components/flow/flow-actions';
import { FlowFormFields } from '@/shared/components/flow/flow-fields';
import { Button } from '@/shared/components/ui/button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card';
import { Label } from '@/shared/components/ui/label';
import { getKpiUnitTranslation } from '@/shared/hooks/use-kpi-unit-translation';

interface CreateProjectKpiSummaryFormProps {
  onSuccess: () => void;
  onBack: () => void;
  onAddMore: () => void;
  onSkip: () => void;
  onChange: (data: CreateProjectStoreSetData) => void;
}

export const CreateProjectKpiSummaryForm = ({
  onSuccess,
  onBack,
  onAddMore,
  onSkip,
  onChange,
}: CreateProjectKpiSummaryFormProps) => {
  const { t } = useTranslation('project', {
    keyPrefix: 'KpiFormEditor',
  });
  const { t: tKpiTypes } = useTranslation('kpiTypes', { nsMode: 'fallback' });

  const { t: tShared } = useTranslation('shared');
  const { t: tCommon } = useTranslation('common');
  const { data } = useCreateProjectStore();
  const kpis = data.kpis || [];

  const onDeleteKpi = (index: number) => {
    if (!kpis) return;
    const newKpis = kpis.filter((_, i) => i !== index);
    onChange({ kpis: newKpis });
  };

  return (
    <>
      <CreateProjectHeader>
        <FlowSkipStepButton onClick={onSkip} />
      </CreateProjectHeader>
      <Card variant="form" wrapWithStars>
        <CardHeader>
          <CardTitle>{t('summary_label')}</CardTitle>
        </CardHeader>
        <CardContent>
          <FlowFormFields>
            <BorderGradient className="divide-y divide-white/30 bg-custom-soft-purple">
              {kpis?.length === 0 && (
                <div className="p-4 text-center text-muted-foreground">
                  {t('no_kpis_placeholder')}
                </div>
              )}
              {kpis?.map((kpi, index) => (
                <div
                  key={index}
                  className="flex items-center justify-between gap-4 p-4"
                >
                  <div className="flex flex-1 flex-row items-center justify-between gap-4">
                    <div>
                      <Label className="text-sm font-normal text-white">
                        {kpi.name}
                      </Label>

                      <div className="text-2xs font-normal text-custom-grey">
                        {tShared(kpi.type)} -{' '}
                        {getKpiUnitTranslation(tKpiTypes, kpi.unit)}
                      </div>
                    </div>
                    <Button
                      btnType="icon"
                      variant="ghost"
                      className="size-5 rounded-full bg-white text-accent-foreground"
                      onClick={() => onDeleteKpi(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </BorderGradient>
            <div className="my-6 flex justify-end">
              <Button
                className="border border-white/15 bg-transparent p-2 text-white"
                variant={'outline'}
                btnType="secondary"
                onClick={onAddMore}
              >
                <PlusWhiteIcon className="h-4 w-4 fill-white text-white" />
                {t('add_kpi_button_label')}
              </Button>
            </div>
          </FlowFormFields>
        </CardContent>
        <CardFooter className="flex justify-between">
          <FlowBackButton onClick={onBack}>
            {tCommon('button.back')}
          </FlowBackButton>
          <Button
            className="w-40"
            variant={'greenGradient'}
            btnType="primary"
            onClick={onSuccess}
          >
            {t('submit_button_label')}
          </Button>
        </CardFooter>
      </Card>
    </>
  );
};
